import { useCallback, useEffect, useRef, useState } from "react";
import { type AppCallbacks, MainService } from "../../main-service";
import { useSession } from "../context/SessionContext";
import { useTranscription } from "../context/TranscriptionContext";

export const useMonologLive = () => {
	const { session, setSession } = useSession();
	const { dispatch: transDispatch } = useTranscription();
	const [isSummaryOpen, setIsSummaryOpen] = useState(false);
	const [isGeneratingComments, setIsGeneratingComments] = useState(false);
	const [isStopping, setIsStopping] = useState(false);

	// Singleton instance of the core app logic
	const serviceRef = useRef<MainService | null>(null);

	useEffect(() => {
		if (!serviceRef.current) {
			const callbacks: AppCallbacks = {
				onStatusChange: (status, statusType) => {
					setSession((prev) => ({ ...prev, status, statusType }));
				},
				onTranscriptionUpdate: (segment) => {
					transDispatch({ type: "ADD_SEGMENT", segment });
				},
				onTranscriptionBulkUpdate: (segments) => {
					transDispatch({ type: "SET_SEGMENTS", segments });
				},
				onTranscriptionClear: () => {
					transDispatch({ type: "CLEAR_SEGMENTS" });
				},
				onCommentsUpdate: (newComments) => {
					setSession((prev) => ({ ...prev, comments: newComments }));
				},
				onSessionStateChange: (isRunning, isPaused) => {
					setSession((prev) => ({ ...prev, isRunning, isPaused }));
				},
				onCommenterDefinitionsUpdate: (definitions) => {
					setSession((prev) => ({
						...prev,
						commenterDefinitions: definitions,
					}));
				},
				onLowConfidenceThresholdUpdate: (threshold) => {
					setSession((prev) => ({
						...prev,
						lowConfidenceThreshold: threshold,
					}));
				},
				onSummaryGenerated: (summary, lastSession) => {
					setSession((prev) => ({ ...prev, summary, lastSession }));
					setIsSummaryOpen(true);
				},
				onSessionIdChange: (currentSessionId) => {
					setSession((prev) => ({ ...prev, currentSessionId }));
				},
			};

			serviceRef.current = new MainService(callbacks);
			serviceRef.current.initialize();
		}

		return () => {
			if (serviceRef.current) {
				serviceRef.current.destroy();
				serviceRef.current = null;
			}
		};
	}, [setSession, transDispatch]);

	const handleStart = useCallback(() => {
		serviceRef.current?.startSession();
	}, []);

	const handlePause = useCallback(() => {
		if (session.isPaused) {
			serviceRef.current?.resumeSession();
		} else {
			serviceRef.current?.pauseSession();
		}
	}, [session.isPaused]);

	const handleStop = useCallback(async () => {
		if (!serviceRef.current || isStopping) return;

		try {
			setIsStopping(true);
			await serviceRef.current.stopSession();
		} finally {
			setIsStopping(false);
		}
	}, [isStopping]);

	const generateComments = useCallback(async () => {
		if (!serviceRef.current || isGeneratingComments) return;

		try {
			setIsGeneratingComments(true);
			await serviceRef.current.generateComments();
		} finally {
			setIsGeneratingComments(false);
		}
	}, [isGeneratingComments]);

	const handleCommentClick = useCallback((id: string) => {
		serviceRef.current?.handleCommentClick(id);
	}, []);

	const handleResumeExistingSession = useCallback(async (sessionId: string) => {
		await serviceRef.current?.resumeExistingSession(sessionId);
	}, []);

	return {
		service: serviceRef.current,
		handleStart,
		handlePause,
		handleStop,
		handleResumeExistingSession,
		generateComments,
		isGeneratingComments,
		isStopping,
		handleCommentClick,
		isSummaryOpen,
		setIsSummaryOpen,
	};
};
